//OtherUsersBookShelfPage.tsx
// 「本棚」パネル。ユーザーページ（UserPage.tsx）の「本棚」タブに埋め込まれる
// 閲覧専用コンポーネント（並び替え・フィルターはできるが追加・削除・カスタム並び替え編集はできない）。
// 旧 /bookshelf/:userNameOnURL ページの中身をそのまま移設したもの
// （ページ全体のクロム・プロフィールタイトルは親のUserPage側が持つため、ここでは持たない）。

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { NEW_API } from '../config/api';
import { generateCoverUrlSmart } from '../utils/generateCoverUrl';
import { useAppTokens } from '../context/ThemeModeContext';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';

import { getUserSubFromUsernameOpenEndpoint } from '../utils/getUserSubFromUsername';

interface Shelf {
  shelf_id: string;
  shelf_name: string;
  color_code: string;
  order: number;
  book_count: number;
  custom_order_updated_at_ms: number | null;
}

interface Book {
  global_book_id: string;
  rl_book_id: number;
  shelf_id: string | null;
  registered_at: string;
  title: string;
  author: string;
  isbn13: string;
  pub_year: string;
}

const ALL_SHELF_ID = 'all';

interface OtherUsersBookshelfPanelProps {
  userNameOnURL: string;
  // 公開本棚数・全公開冊数が確定したら呼ばれる（親ページのミニヘッダー統計表示用）
  onStatsLoaded?: (stats: { shelfCount: number; bookCount: number }) => void;
}

const OtherUsersBookshelfPanel: React.FC<OtherUsersBookshelfPanelProps> = ({ userNameOnURL, onStatsLoaded }) => {
  const TOKENS = useAppTokens();
  const [books, setBooks] = useState<Book[]>([]);
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [selectedShelf, setSelectedShelf] = useState<string>(ALL_SHELF_ID);
  const [sortCriteria, setSortCriteria] = useState<string>('newest');
  const [customOrderCache, setCustomOrderCache] = useState<Record<string, string[]>>({});
  const [filterText, setFilterText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUserBookshelfData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // ★ここは変えない
      const targetUserSub = await getUserSubFromUsernameOpenEndpoint(userNameOnURL || '');
      if (!targetUserSub) {
        setError('Failed to fetch user information');
        return;
      }

      // 本棚メタ情報（公開本棚のみ、冊数付き）。認証不要
      const shelvesRes = await axios.get(NEW_API.SHELVES, {
        params: { public_user_sub: targetUserSub },
      });
      const shelfItems: any[] = shelvesRes.data?.data?.items ?? [];
      const newShelves: Shelf[] = shelfItems
        .map((s) => ({
          shelf_id: s.rl_global_shelf_id,
          shelf_name: s.global_shelf_name ?? '',
          color_code: s.global_shelf_color_code || '#FFFFFF',
          order: typeof s.global_shelf_order === 'number' ? s.global_shelf_order : 999,
          book_count: Number(s.book_count) || 0,
          custom_order_updated_at_ms: s.custom_order_updated_at_ms ?? null,
        }))
        .sort((a, b) => a.order - b.order);
      setShelves(newShelves);

      // 公開本一覧（書誌情報マージ済み、除籍済みは含まれない）をページごとに取得。認証不要
      // 1ページ目取得時点でローディング解除し、以降は逐次描画で流し込む
      //（全ページ取得を待たずに本棚を表示 → 蔵書が多くても即座に使える）
      const allBooks: Book[] = [];
      let cursor: string | null = null;
      let isFirstPage = true;
      do {
        const params: Record<string, string | number> = {
          public_user_sub: targetUserSub,
          limit: 100,
        };
        if (cursor) params.cursor = cursor;
        const res = await axios.get(NEW_API.BOOKS, { params });
        const data = res.data?.data ?? {};
        const items: any[] = data.items ?? [];

        items.forEach((item) => {
          allBooks.push({
            global_book_id: item.global_book_id,
            rl_book_id: item.rl_book_id,
            shelf_id: item.rl_global_shelf_id ?? null,
            registered_at: item.global_book_registered_at ?? '',
            title: item.title ?? '(タイトル情報なし)',
            author: item.author ?? '',
            isbn13: item.isbn13 ?? '',
            pub_year: item.pub_year != null ? String(item.pub_year) : '',
          });
        });

        setBooks([...allBooks]); // ページ取得ごとに逐次描画
        cursor = data.next_cursor ?? null;

        if (isFirstPage) {
          isFirstPage = false;
          setIsLoading(false);
          setIsLoadingMore(Boolean(cursor));
        }
      } while (cursor);

      setIsLoadingMore(false);
      onStatsLoaded?.({ shelfCount: newShelves.length, bookCount: allBooks.length });
    } catch (err) {
      setError('An error occurred while fetching the bookshelf data');
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [userNameOnURL, onStatsLoaded]);

  // 本棚ごとの冊数を計算（「すべての本」は全公開冊数）
  const shelfBookCounts = useMemo(() => {
    const counts: { [key: string]: number } = { [ALL_SHELF_ID]: books.length };
    shelves.forEach((shelf) => {
      counts[shelf.shelf_id] = shelf.book_count;
    });
    return counts;
  }, [books, shelves]);

  useEffect(() => {
    if (userNameOnURL) {
      fetchUserBookshelfData();
    }
  }, [userNameOnURL, fetchUserBookshelfData]);

  // 公開本棚のカスタム並び順を取得してキャッシュに乗せる（認証不要、非公開本棚は404）
  const fetchPublicCustomOrder = useCallback(async (shelfId: string): Promise<string[]> => {
    try {
      const res = await axios.get(NEW_API.SHELVES, {
        params: { public_shelf_id: shelfId, book_order: 'true' },
      });
      const items: any[] = res.data?.data?.items ?? [];
      const orderedIds = items.map((i) => i.global_book_id as string);
      setCustomOrderCache((prev) => ({ ...prev, [shelfId]: orderedIds }));
      return orderedIds;
    } catch (err) {
      return [];
    }
  }, []);

  // 本棚を開いたとき、カスタム並び順が設定されていればそれを優先して表示する
  // （本棚の持ち主が意図した順番を、閲覧者にもデフォルトで見せる）
  useEffect(() => {
    if (selectedShelf === ALL_SHELF_ID) return;
    const shelf = shelves.find((s) => s.shelf_id === selectedShelf);
    if (!shelf || shelf.custom_order_updated_at_ms == null) return;
    setSortCriteria('custom');
    if (!customOrderCache[selectedShelf]) {
      fetchPublicCustomOrder(selectedShelf);
    }
  }, [selectedShelf, shelves, customOrderCache, fetchPublicCustomOrder]);

  const handleBookClick = (book: Book) => {
    window.open(`/book/${book.rl_book_id}`, '_blank', 'noopener,noreferrer');
  };

  const sortBooks = (books: Book[]) => {
    switch (sortCriteria) {
      case 'newest':
        return books.sort(
          (a, b) => new Date(b.registered_at).getTime() - new Date(a.registered_at).getTime()
        );
      case 'oldest':
        return books.sort(
          (a, b) => new Date(a.registered_at).getTime() - new Date(b.registered_at).getTime()
        );
      case 'pub-newest':
        return books.sort((a, b) => Number(b.pub_year || 0) - Number(a.pub_year || 0));
      case 'pub-oldest':
        return books.sort((a, b) => Number(a.pub_year || 0) - Number(b.pub_year || 0));
      case 'custom': {
        const orderList = customOrderCache[selectedShelf] ?? [];
        const indexMap = new Map(orderList.map((id, i) => [id, i]));
        return books.sort((a, b) => {
          const ia = indexMap.get(a.global_book_id);
          const ib = indexMap.get(b.global_book_id);
          if (ia !== undefined && ib !== undefined) return ia - ib;
          if (ia !== undefined) return -1;
          if (ib !== undefined) return 1;
          return new Date(b.registered_at).getTime() - new Date(a.registered_at).getTime();
        });
      }
      default:
        return books;
    }
  };

  const filterBooks = (book: Book) => {
    const searchText = filterText.toLowerCase();
    return (
      book.title.toLowerCase().includes(searchText) ||
      book.author.toLowerCase().includes(searchText)
    );
  };

  const filteredBooks = useMemo(() => {
    return books
      .filter((book) => selectedShelf === ALL_SHELF_ID || book.shelf_id === selectedShelf)
      .filter(filterBooks);
  }, [books, selectedShelf, filterText]);

  const sortedBooks = useMemo(() => {
    // sortBooksは破壊的ソートなのでコピーしてから
    return sortBooks([...filteredBooks]);
  }, [filteredBooks, sortCriteria, customOrderCache, selectedShelf]);

  const getSelectedShelfColor = () => {
    if (selectedShelf === ALL_SHELF_ID) return TOKENS.accent;
    const selected = shelves.find((shelf) => shelf.shelf_id === selectedShelf);
    return selected ? selected.color_code : TOKENS.accent;
  };

  const selectedShelfName =
    selectedShelf === ALL_SHELF_ID
      ? 'すべての本'
      : shelves.find((s) => s.shelf_id === selectedShelf)?.shelf_name ?? '';

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: TOKENS.text, py: 4 }}>
        <CircularProgress size={20} sx={{ color: TOKENS.textSub }} />
        <Typography sx={{ fontSize: 13, color: TOKENS.textSub }}>本棚を読み込んでいます...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography sx={{ fontSize: 13, color: TOKENS.error }}>{error}</Typography>
      </Box>
    );
  }

  if (books.length === 0) {
    return (
      <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, textAlign: 'center', py: 6 }}>
        公開されている本棚がありません。
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, color: TOKENS.text }}>
        {/* Top status + controls (glass panel) */}
        <Box
          sx={{
            border: `1px solid ${TOKENS.border}`,
            background: TOKENS.glass,
            borderRadius: '16px',
            p: 1.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.25 }}>
            <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>
              {books.length}冊
            </Typography>
            {isLoadingMore && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <CircularProgress size={12} sx={{ color: TOKENS.textSub }} />
                <Typography sx={{ fontSize: 11.5, color: TOKENS.textSub }}>読み込み中...</Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <FormControl
              variant="outlined"
              size="small"
              sx={{
                minWidth: 180,
                '& .MuiOutlinedInput-root': {
                  color: TOKENS.text,
                  background: TOKENS.buttonBg,
                  borderRadius: '12px'
                },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.borderSoft },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.accent },
                '& .MuiSvgIcon-root': { color: TOKENS.textSub },
                '& .MuiInputLabel-root': { color: TOKENS.textSub },
                '& .MuiInputLabel-root.Mui-focused': { color: TOKENS.text }
              }}
            >
              <InputLabel>並び替え</InputLabel>
              <Select
                value={sortCriteria}
                onChange={(e) => setSortCriteria(e.target.value as string)}
                label="並び替え"
              >
                <MenuItem value="newest">登録順(新しい順)</MenuItem>
                <MenuItem value="oldest">登録順(古い順)</MenuItem>
                <MenuItem value="pub-newest">出版日順(新しい順)</MenuItem>
                <MenuItem value="pub-oldest">出版日順(古い順)</MenuItem>
                <MenuItem
                  value="custom"
                  disabled={
                    selectedShelf === ALL_SHELF_ID ||
                    shelves.find((s) => s.shelf_id === selectedShelf)?.custom_order_updated_at_ms == null
                  }
                >
                  カスタム
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="フィルター"
              size="small"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              variant="outlined"
              sx={{
                minWidth: { xs: '100%', sm: 260 },
                '& .MuiOutlinedInput-root': {
                  color: TOKENS.text,
                  background: TOKENS.buttonBg,
                  borderRadius: '12px'
                },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.borderSoft },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.accent },
                '& .MuiInputLabel-root': { color: TOKENS.textSub },
                '& .MuiInputLabel-root.Mui-focused': { color: TOKENS.text }
              }}
              InputProps={{
                endAdornment: filterText ? (
                  <IconButton
                    onClick={() => setFilterText('')}
                    size="small"
                    sx={{
                      color: TOKENS.textSub,
                      '&:hover': { color: TOKENS.text, background: 'rgba(148,163,184,0.10)' }
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : null
              }}
            />
          </Box>
        </Box>

        {/* Main split layout: 親(UserPage)ごとページ全体でスクロールするため内部スクロールは持たない */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'flex-start',
            flexDirection: { xs: 'column', md: 'row' }
          }}
        >
          {/* Left panel (shelves) */}
          <Box
            sx={{
              width: { xs: '100%', md: 260 },
              flexShrink: 0,
              border: `1px solid ${TOKENS.border}`,
              background: TOKENS.buttonBg,
              borderRadius: '16px',
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                borderBottom: `1px solid ${TOKENS.border}`,
                background: TOKENS.glass
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: TOKENS.text, letterSpacing: 0.2 }}>
                棚
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: TOKENS.textSub, mt: 0.25 }}>
                公開されている本棚のみ表示
              </Typography>
            </Box>

            <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
              <List sx={{ py: 0 }}>
                <ListItem key={ALL_SHELF_ID} disablePadding>
                  <ListItemButton
                    selected={selectedShelf === ALL_SHELF_ID}
                    onClick={() => setSelectedShelf(ALL_SHELF_ID)}
                    sx={{
                      px: 1.5,
                      py: 1.0,
                      alignItems: 'flex-start',
                      borderLeft: selectedShelf === ALL_SHELF_ID ? `3px solid ${TOKENS.accent}` : '3px solid transparent',
                      '&.Mui-selected': { background: 'rgba(148,163,184,0.10)' },
                      '&.Mui-selected:hover': { background: 'rgba(148,163,184,0.14)' },
                      '&:hover': { background: 'rgba(148,163,184,0.08)' }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                          <Typography
                            component="span"
                            sx={{
                              fontSize: 12.5,
                              color: TOKENS.text,
                              fontWeight: selectedShelf === ALL_SHELF_ID ? 700 : 500
                            }}
                          >
                            すべての本
                          </Typography>
                          <Typography component="span" sx={{ fontSize: 11.5, color: TOKENS.textSub }}>
                            ({shelfBookCounts[ALL_SHELF_ID] || 0})
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                {shelves.map((shelf) => {
                  const isSelected = selectedShelf === shelf.shelf_id;
                  const count = shelfBookCounts[shelf.shelf_id] || 0;
                  return (
                    <ListItem key={shelf.shelf_id} disablePadding>
                      <ListItemButton
                        selected={isSelected}
                        onClick={() => setSelectedShelf(shelf.shelf_id)}
                        sx={{
                          px: 1.5,
                          py: 1.0,
                          alignItems: 'flex-start',
                          borderLeft: isSelected ? `3px solid ${shelf.color_code}` : '3px solid transparent',
                          '&.Mui-selected': { background: 'rgba(148,163,184,0.10)' },
                          '&.Mui-selected:hover': { background: 'rgba(148,163,184,0.14)' },
                          '&:hover': { background: 'rgba(148,163,184,0.08)' }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: 12.5,
                                  lineHeight: '1.25em',
                                  color: TOKENS.text,
                                  fontWeight: isSelected ? 700 : 500
                                }}
                              >
                                {shelf.shelf_name}
                              </Typography>
                              <Typography component="span" sx={{ fontSize: 11.5, color: TOKENS.textSub }}>
                                ({count})
                              </Typography>
                            </Box>
                          }
                          sx={{
                            m: 0,
                            '& .MuiListItemText-primary': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          </Box>

          {/* Right panel (books) */}
          <Box
            sx={{
              flexGrow: 1,
              minWidth: 0,
              border: `1px solid ${TOKENS.border}`,
              background: TOKENS.buttonBg,
              borderRadius: '16px',
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                borderBottom: `1px solid ${TOKENS.border}`,
                background: TOKENS.glass,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap'
              }}
            >
              <Box
                sx={{
                  borderRadius: '999px',
                  px: 1.25,
                  py: 0.6,
                  background: TOKENS.glass,
                  border: `1px solid ${TOKENS.borderSoft}`,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '999px',
                    background: getSelectedShelfColor(),
                    opacity: 0.85,
                    boxShadow: '0 0 0 2px rgba(2,6,23,0.35)'
                  }}
                />
                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{selectedShelfName || '表示'}</Typography>
              </Box>

              <Typography sx={{ fontSize: 11.5, color: TOKENS.textSub }}>{sortedBooks.length} 件</Typography>
            </Box>

            <Box sx={{ p: 1.5 }}>
              <Grid container spacing={2} columns={{ xs: 2, sm: 4, md: 4, lg: 6, xl: 6 }}>
                {sortedBooks.map((book) => (
                  <Grid item xs={1} sm={1} md={1} lg={1} xl={1} key={book.global_book_id}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <Card
                        onClick={() => handleBookClick(book)}
                        sx={{
                          height: 230,
                          display: 'flex',
                          flexDirection: 'column',
                          background: TOKENS.buttonBg,
                          border: `1px solid ${TOKENS.borderSoft}`,
                          borderRadius: '14px',
                          overflow: 'hidden',
                          transition: '0.18s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: TOKENS.accent,
                            transform: 'translateY(-1px)'
                          }
                        }}
                      >
                        <Box
                          sx={{
                            flexGrow: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 1.25
                          }}
                        >
                          <CardMedia
                            component="img"
                            sx={{
                              height: 170,
                              objectFit: 'contain',
                              borderRadius: '8px'
                            }}
                            image={generateCoverUrlSmart(book.rl_book_id, book.title, book.isbn13)}
                            alt={book.title}
                          />
                        </Box>
                      </Card>

                      <Box sx={{ mt: 1, textAlign: 'left', px: 0.25 }}>
                        <Typography
                          sx={{ fontSize: 12.5, fontWeight: 600, color: TOKENS.text }}
                          noWrap
                          title={book.title}
                        >
                          {book.title}
                        </Typography>
                        <Typography
                          sx={{ fontSize: 11.5, color: TOKENS.textSub }}
                          noWrap
                          title={book.author}
                        >
                          {book.author}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        </Box>
    </Box>
  );
};

export default OtherUsersBookshelfPanel;
